import type { BehaviorNode } from "../blueprint/types.js";
import { normalizeShortcut } from "../blueprint/shortcut-keys.js";
import { compileFlowFrom } from "./compileFlow.js";
import { uniqueDependencies } from "./method.js";
import type {
  CompileFlowOptions,
  CompileNodeContext,
  CompiledBehavior,
  CompiledEventHandler,
  FlowRunResult,
  IndexedGraph,
  RuntimeContext,
  SceneCatalogDependencyIndex,
} from "./types.js";

export type { CompiledEventHandler };

function isLayoutListNode(node: unknown): boolean {
  if (!node || typeof node !== "object") {
    return false;
  }
  const candidate = node as {
    kind?: unknown;
    component?: unknown;
  };
  return candidate.kind === "component" && candidate.component === "layoutList";
}

function resolveListFilterEventTargetListId(
  indexed: IndexedGraph,
  node: BehaviorNode,
  layoutNodeById?: Map<string, unknown>,
): string | undefined {
  if (
    node.type !== "event" ||
    (node.kind !== "onListFilterExit" && node.kind !== "onListFilterEnter")
  ) {
    return undefined;
  }

  const targetInputConnection = indexed.inputConnectionByNodeAndPort.get(
    `${node.id}:target`,
  );
  if (!targetInputConnection) {
    return undefined;
  }

  const sourceNode = indexed.nodesById.get(targetInputConnection.fromNodeId);
  const sourcePort = sourceNode?.ports.find(
    (port) => port.id === targetInputConnection.fromPortId,
  );
  const sourceValue =
    sourcePort?.value && typeof sourcePort.value === "object"
      ? (sourcePort.value as { kind?: unknown; nodeId?: unknown })
      : undefined;
  if (
    sourceValue?.kind !== "layoutList" ||
    typeof sourceValue.nodeId !== "string" ||
    sourceValue.nodeId.trim().length === 0
  ) {
    return undefined;
  }

  if (!layoutNodeById) {
    return sourceValue.nodeId;
  }

  const explicitNode = layoutNodeById.get(sourceValue.nodeId);
  return isLayoutListNode(explicitNode) ? sourceValue.nodeId : undefined;
}

export function getEventWatchDependencies(node: BehaviorNode): string[] {
  if (node.type !== "event" || !("kind" in node) || node.kind !== "onChange") {
    return [];
  }

  const source = node.objectSource;
  if (!source) {
    return [];
  }

  const path = node.objectFieldPath;
  return path ? [`${source}.${path}`] : [source];
}

function getEventOutputPortId(node: BehaviorNode): string {
  const outputPort = node.ports.find((port) => port.direction === "output");
  return outputPort?.id ?? "out";
}

function getControlRoomCombo(node: BehaviorNode): string | undefined {
  if (node.type !== "event" || node.kind !== "onControlRoom") {
    return undefined;
  }

  const combo = normalizeShortcut(
    node.shortcutKey1 ?? "",
    node.shortcutKey2,
    node.shortcutKey3,
  );
  return combo || undefined;
}

/**
 * Compiles one flow handler per event node in a layout behavior graph.
 */
export function compileEventHandlers(
  indexed: IndexedGraph,
  ownerNodeId: string,
  catalogDeps?: SceneCatalogDependencyIndex,
  flowOptions?: CompileFlowOptions,
  layoutNodeById?: Map<string, unknown>,
): CompiledEventHandler[] {
  const handlers: CompiledEventHandler[] = [];

  for (const node of indexed.nodesById.values()) {
    if (node.type !== "event") {
      continue;
    }

    const rawKind = "kind" in node ? String(node.kind) : "event";
    const eventKind =
      rawKind === "onCustomSceneEvent" ? "customEvent" : rawKind;
    const outputPortId = getEventOutputPortId(node);

    handlers.push({
      eventNodeId: node.id,
      eventKind,
      ...(rawKind === "onCustomSceneEvent" && "customEventId" in node
        ? { customEventId: node.customEventId }
        : {}),
      flow: compileFlowFrom(
        indexed,
        ownerNodeId,
        node.id,
        outputPortId,
        catalogDeps,
        flowOptions,
      ),
      watchDependencies: getEventWatchDependencies(node),
      triggerOnInit:
        rawKind === "onChange" && "triggerOnInit" in node
          ? node.triggerOnInit === true
          : undefined,
      controlRoomCombo: getControlRoomCombo(node),
      shortcutBinding:
        node.kind === "onControlRoom" ? node.shortcutBinding : undefined,
      targetListNodeId: resolveListFilterEventTargetListId(
        indexed,
        node,
        layoutNodeById,
      ),
    });
  }

  return handlers;
}

export function collectBehaviorDependencies(
  behavior: CompiledBehavior,
  compileContext?: CompileNodeContext,
): string[] {
  const customEventDeps = behavior.eventHandlers.flatMap((handler) => {
    if (handler.eventKind !== "customEvent" || !handler.customEventId) {
      return [];
    }

    return (
      compileContext?.customEventTriggerDependenciesById?.[
        handler.customEventId
      ] ?? []
    );
  });

  return uniqueDependencies([
    ...behavior.eventHandlers.flatMap((handler) => handler.flow.dependencies),
    ...behavior.eventHandlers.flatMap(
      (handler) => handler.watchDependencies ?? [],
    ),
    ...customEventDeps,
    ...(behavior.rowVariantRules ?? []).flatMap((rule) => rule.dependencies),
    ...(behavior.listFilterRules ?? []).flatMap((rule) => rule.dependencies),
    ...(behavior.listSortRules ?? []).flatMap((rule) => rule.dependencies),
  ]);
}

/**
 * Runs compiled flows for the given event kinds and returns layout patches.
 */
export function runFlowsForEventKinds(
  behavior: CompiledBehavior,
  ctx: RuntimeContext,
  kinds: string[],
): FlowRunResult {
  const result: FlowRunResult = {
    patches: [],
    effects: [],
    continuations: [],
  };

  for (const handler of behavior.eventHandlers) {
    if (!kinds.includes(handler.eventKind)) {
      continue;
    }

    const flowResult = handler.flow.run(ctx);
    result.patches.push(...flowResult.patches);
    result.effects.push(...flowResult.effects);
    result.continuations.push(...flowResult.continuations);
  }

  return result;
}

/** Runs `onLoad` event flows (initial layout evaluation). */
export function runInitialFlows(
  behavior: CompiledBehavior,
  ctx: RuntimeContext,
): FlowRunResult {
  return runFlowsForEventKinds(behavior, ctx, ["onLoad"]);
}

/** Runs `onChange` event flows (reactive data updates). */
export function runOnChangeFlows(
  behavior: CompiledBehavior,
  ctx: RuntimeContext,
): FlowRunResult {
  return runFlowsForEventKinds(behavior, ctx, ["onChange"]);
}
